// client/src/components/wholesaler/SubcategoryManager.js
// ================================================================
// SUBCATEGORY MANAGER — Modal component for managing wholesaler subcategories
// Supports both wholesaler and admin mode with multi-select + bulk delete
// ================================================================
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { wholesalerAPI, adminAPI, registryAPI } from '../../services/api';
import ConfirmModal from '../common/ConfirmModal';

const SubcategoryManager = ({ isOpen, onClose, onSubcategoriesChanged, adminMode = false, wholesalerId = null }) => {
  const { t, i18n } = useTranslation(['wholesaler', 'products']);
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', icon: 'warning', confirmLabel: 'Confirm', confirmColor: 'red', onConfirm: () => {} });
  const openConfirm = (opts) => setConfirmModal({ isOpen: true, ...opts });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  // New subcategory form
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState({
    parentCategory: '',
    key: '',
    label_en: '',
    label_el: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelected(new Set());
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const lang = i18n.language === 'el' ? 'el' : 'en';
      const [subRes, catRes] = await Promise.all([
        adminMode ? adminAPI.getWholesalerSubcategories(wholesalerId) : wholesalerAPI.getSubcategories(),
        registryAPI.getCategories(lang)
      ]);
      setSubcategories(subRes.data?.data || []);
      setCategories(catRes.data?.data || []);
    } catch (err) {
      console.error('Failed to load:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ parentCategory: '', key: '', label_en: '', label_el: '' });
    setShowForm(false);
    setEditingKey(null);
    setError(null);
  };

  const handleCreate = async () => {
    if (!form.parentCategory || !form.label_en) {
      setError(t('wholesaler:subcategories.fillRequired', 'Parent category and English label are required'));
      return;
    }

    const key = form.key || form.label_en.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    try {
      setSaving(true);
      setError(null);
      const data = {
        parentCategory: form.parentCategory,
        key,
        label_en: form.label_en,
        label_el: form.label_el || form.label_en
      };
      if (adminMode) {
        await adminAPI.createWholesalerSubcategory(wholesalerId, data);
      } else {
        await wholesalerAPI.createSubcategory(data);
      }
      resetForm();
      fetchData();
      if (onSubcategoriesChanged) onSubcategoriesChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create subcategory');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingKey || !form.label_en) return;
    try {
      setSaving(true);
      setError(null);
      await wholesalerAPI.updateSubcategory(editingKey, {
        parentCategory: form.parentCategory,
        label_en: form.label_en,
        label_el: form.label_el
      });
      resetForm();
      fetchData();
      if (onSubcategoriesChanged) onSubcategoriesChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sub) => {
    openConfirm({
      title: t('wholesaler:subcategories.deleteTitle', 'Delete Subcategory'),
      message: t('wholesaler:subcategories.confirmDelete', `Delete "${sub.label_en}"? Products with this subcategory will be cleared.`),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          if (adminMode) {
            await adminAPI.deleteWholesalerSubcategory(wholesalerId, sub.key, sub.parentCategory);
          } else {
            await wholesalerAPI.deleteSubcategory(sub.key, sub.parentCategory);
          }
          fetchData();
          if (onSubcategoriesChanged) onSubcategoriesChanged();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to delete');
        }
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    openConfirm({
      title: t('wholesaler:subcategories.bulkDeleteTitle', 'Delete Subcategories'),
      message: t('wholesaler:subcategories.confirmBulkDelete', `Delete ${count} subcategories? Products with these subcategories will be cleared.`).replace('{{count}}', count),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          setBulkDeleting(true);
          const items = Array.from(selected).map(id => {
            const [parentCategory, key] = id.split('::');
            return { key, parentCategory };
          });

          if (adminMode) {
            await adminAPI.bulkDeleteWholesalerSubcategories(wholesalerId, items);
          } else {
            await wholesalerAPI.bulkDeleteSubcategories(items);
          }
          setSelected(new Set());
          fetchData();
          if (onSubcategoriesChanged) onSubcategoriesChanged();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to bulk delete');
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  const toggleSelect = (sub) => {
    const id = `${sub.parentCategory}::${sub.key}`;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectGroup = (parentKey, subs) => {
    setSelected(prev => {
      const next = new Set(prev);
      const ids = subs.map(s => `${s.parentCategory}::${s.key}`);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const startEdit = (sub) => {
    setEditingKey(sub.key);
    setForm({
      parentCategory: sub.parentCategory,
      key: sub.key,
      label_en: sub.label_en,
      label_el: sub.label_el
    });
    setShowForm(true);
  };

  const getCategoryLabel = (key) => {
    const cat = categories.find(c => c.key === key);
    return cat ? `${cat.icon} ${cat.label}` : key;
  };

  // Group subcategories by parent
  const grouped = subcategories.reduce((acc, sub) => {
    if (!acc[sub.parentCategory]) acc[sub.parentCategory] = [];
    acc[sub.parentCategory].push(sub);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{t('wholesaler:subcategories.title', 'Manage Subcategories')}</h2>
            <p className="text-sm text-blue-100">{t('wholesaler:subcategories.subtitle', 'Create custom subcategories to organize your products')}</p>
          </div>
          <button onClick={() => { resetForm(); onClose(); }} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <span className="text-xl">✕</span>
          </button>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span className="text-sm text-red-700 font-medium">
              {selected.size} {t('wholesaler:subcategories.selected', 'selected')}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {t('common:cancel', 'Cancel')}
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? '...' : t('wholesaler:subcategories.deleteSelected', `Delete ${selected.size}`).replace('{{count}}', selected.size)}
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              {/* Create / Edit Form */}
              {showForm ? (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-3">
                    {editingKey ? t('wholesaler:subcategories.edit', 'Edit Subcategory') : t('wholesaler:subcategories.create', 'New Subcategory')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Parent Category */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('wholesaler:subcategories.parentCategory', 'Parent Category')} *
                      </label>
                      <select
                        value={form.parentCategory}
                        onChange={(e) => setForm({ ...form, parentCategory: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        disabled={!!editingKey}
                      >
                        <option value="">{t('wholesaler:subcategories.selectParent', 'Select...')}</option>
                        {categories.map(cat => (
                          <option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* English Label */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('wholesaler:subcategories.labelEn', 'English Name')} *
                      </label>
                      <input
                        type="text"
                        value={form.label_en}
                        onChange={(e) => setForm({ ...form, label_en: e.target.value })}
                        placeholder="e.g., Greek Yogurt"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Greek Label */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('wholesaler:subcategories.labelEl', 'Greek Name')}
                      </label>
                      <input
                        type="text"
                        value={form.label_el}
                        onChange={(e) => setForm({ ...form, label_el: e.target.value })}
                        placeholder="π.χ., Ελληνικό Γιαούρτι"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={editingKey ? handleUpdate : handleCreate}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {saving ? '...' : editingKey ? t('common:save', 'Save') : t('common:create', 'Create')}
                    </button>
                    <button onClick={resetForm} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                      {t('common:cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowForm(true)}
                  className="mb-6 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  + {t('wholesaler:subcategories.addNew', 'Add Subcategory')}
                </button>
              )}

              {/* Existing Subcategories */}
              {Object.keys(grouped).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-4xl mb-2">📂</p>
                  <p>{t('wholesaler:subcategories.empty', 'No subcategories yet. Create your first one!')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(grouped).map(([parentKey, subs]) => {
                    const groupIds = subs.map(s => `${s.parentCategory}::${s.key}`);
                    const allGroupSelected = groupIds.every(id => selected.has(id));
                    const someGroupSelected = groupIds.some(id => selected.has(id));

                    return (
                      <div key={parentKey} className="border rounded-xl overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2.5 font-medium text-sm text-gray-700 border-b flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={allGroupSelected}
                            ref={el => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                            onChange={() => toggleSelectGroup(parentKey, subs)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          {getCategoryLabel(parentKey)}
                          <span className="text-xs text-gray-400">({subs.length})</span>
                        </div>
                        <div className="divide-y">
                          {subs.map(sub => {
                            const id = `${sub.parentCategory}::${sub.key}`;
                            return (
                              <div key={sub.key} className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${selected.has(id) ? 'bg-blue-50' : ''}`}>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selected.has(id)}
                                    onChange={() => toggleSelect(sub)}
                                    className="w-4 h-4 text-blue-600 rounded"
                                  />
                                  <div>
                                    <span className="font-medium text-gray-900">{i18n.language === 'el' ? sub.label_el : sub.label_en}</span>
                                    <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{sub.key}</span>
                                    {i18n.language !== 'el' && sub.label_el && sub.label_el !== sub.label_en && (
                                      <span className="ml-2 text-xs text-gray-400">({sub.label_el})</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {!adminMode && (
                                    <button
                                      onClick={() => startEdit(sub)}
                                      className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                                    >
                                      {t('common:edit', 'Edit')}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete(sub)}
                                    className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                                  >
                                    {t('common:delete', 'Delete')}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            {subcategories.length} {t('wholesaler:subcategories.total', 'subcategories total')}
          </p>
          <button onClick={() => { resetForm(); onClose(); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium">
            {t('common:close', 'Close')}
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default SubcategoryManager;